/**
 * Factory functions for creating type-safe API hooks.
 *
 * createApiQuery  — wraps useQuery for GET endpoints
 * createApiMutation — wraps useMutation for POST/PUT/PATCH/DELETE endpoints
 */

import { useQuery, useMutation } from "@tanstack/react-query";
import type { UseQueryOptions, UseMutationOptions } from "@tanstack/react-query";
import type { AxiosResponse } from "axios";
import type { ApiResponse } from "@app/stores/api-manager/type";

// ---------------------------------------------------------------------------
// Query
// ---------------------------------------------------------------------------

export interface ApiQueryConfig<TData, TRequest> {
  queryKey: (req: Partial<TRequest>) => unknown[];
  queryFn: (req: TRequest) => Promise<AxiosResponse<any>>;
  extractData: (response: AxiosResponse<any>) => TData;
  isEnabled: (req: Partial<TRequest>) => boolean;
}

export interface ApiQueryOptions<TData, TRequest> {
  request: Partial<TRequest>;
  options?: Omit<UseQueryOptions<TData, Error>, "queryKey" | "queryFn">;
}

export function createApiQuery<TData, TRequest>(
  config: ApiQueryConfig<TData, TRequest>
) {
  return function useApiQuery({
    request,
    options,
  }: ApiQueryOptions<TData, TRequest>) {
    const isEnabled = config.isEnabled(request);
    return useQuery<TData, Error>({
      queryKey: config.queryKey(request),
      queryFn: async (): Promise<TData> => {
        if (!isEnabled) throw new Error("Required parameters are missing");
        const response = await config.queryFn(request as TRequest);
        return config.extractData(response);
      },
      enabled: isEnabled,
      staleTime: 5 * 1000,
      ...options,
    });
  };
}

// ---------------------------------------------------------------------------
// Mutation
// ---------------------------------------------------------------------------

export interface ApiMutationConfig<TData, TRequest> {
  mutationFn: (req: TRequest) => Promise<AxiosResponse<ApiResponse<TData>>>;
}

export interface ApiMutationHookOptions<TData, TRequest> {
  options?: Omit<
    UseMutationOptions<AxiosResponse<ApiResponse<TData>>, Error, TRequest>,
    "mutationFn"
  >;
}

export function createApiMutation<TData, TRequest>(
  config: ApiMutationConfig<TData, TRequest>
) {
  return function useApiMutation(
    hookOptions?: ApiMutationHookOptions<TData, TRequest>
  ) {
    return useMutation<AxiosResponse<ApiResponse<TData>>, Error, TRequest>({
      mutationFn: (request: TRequest): Promise<AxiosResponse<ApiResponse<TData>>> => {
        return config.mutationFn(request);
      },
      ...hookOptions?.options,
    });
  };
}
